import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BooleanField, EditButton, ShowContextProvider, ShowView, Tab, TabbedShowLayout, TextField, useDataProvider, useRedirect, useShowController } from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import MuiTextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AddToLokaliseButton from '../../Components/AddToLokaliseButton';
import ContextTitle from '../../Components/ContextTitle';
import { formatHolidayValue } from '../../lib/naiveDateTime';
import { typeOptions } from '../HolidayTemplates/constants';
import { scheduleTypeChoices } from '../ScheduleTemplates/constants';

const BoolCell = ({ value }: { value: boolean }) => <TableCell>{value ? '✓' : '✗'}</TableCell>;

const PAID_OPTIONS = [
  { id: '', name: 'All' },
  { id: 'true', name: 'Paid' },
  { id: 'false', name: 'Unpaid' }
];

const CountryConfigsShow = () => {
  const controllerProps = useShowController();
  const { record } = controllerProps;
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const location = useLocation();
  const currentTabId = Number(location.pathname.split('show/')[1]) || 0;

  const [filtersReady, setFiltersReady] = useState(false);

  const [holidays, setHolidays] = useState<any[]>([]);
  const [holidayFilter, setHolidayFilter] = useState({ country: '', type: '', paid: '' });

  const [schedules, setSchedules] = useState<any[]>([]);
  const [scheduleFilter, setScheduleFilter] = useState({ country: '', scheduleType: '' });

  const [policies, setPolicies] = useState<any[]>([]);
  const [policyFilter, setPolicyFilter] = useState({ country: '', type: '' });

  const [requestTypes, setRequestTypes] = useState<any[]>([]);
  const [requestTypeFilter, setRequestTypeFilter] = useState({ country: '' });

  const [requestSubtypes, setRequestSubtypes] = useState<any[]>([]);
  const [requestSubtypeFilter, setRequestSubtypeFilter] = useState({ country: '', requestTypeTemplateUuid: '' });

  // Seed filters from record once it loads
  useEffect(() => {
    if (record?.country && !filtersReady) {
      const country = String(record.country);
      setHolidayFilter((f) => ({ ...f, country }));
      setScheduleFilter((f) => ({ ...f, country }));
      setPolicyFilter((f) => ({ ...f, country }));
      setRequestTypeFilter((f) => ({ ...f, country }));
      setRequestSubtypeFilter((f) => ({ ...f, country }));
      setFiltersReady(true);
    }
  }, [record?.country, filtersReady]);

  useEffect(() => {
    if (currentTabId !== 1 || !filtersReady) return;
    const filter: Record<string, string> = {};
    if (holidayFilter.country) filter.country = holidayFilter.country;
    if (holidayFilter.type) filter.type = holidayFilter.type;
    if (holidayFilter.paid !== '') filter.paid = holidayFilter.paid;
    dataProvider.getList('holiday-templates', { filter, pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } }).then(({ data }) => setHolidays(data));
  }, [currentTabId, holidayFilter, filtersReady, dataProvider]);

  useEffect(() => {
    if (currentTabId !== 2 || !filtersReady) return;
    const filter: Record<string, string> = {};
    if (scheduleFilter.country) filter.country = scheduleFilter.country;
    if (scheduleFilter.scheduleType) filter.scheduleType = scheduleFilter.scheduleType;
    dataProvider.getList('schedule-templates', { filter, pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } }).then(({ data }) => setSchedules(data));
  }, [currentTabId, scheduleFilter, filtersReady, dataProvider]);

  useEffect(() => {
    if (currentTabId !== 3 || !filtersReady) return;
    const filter: Record<string, string> = {};
    if (policyFilter.country) filter.country = policyFilter.country;
    if (policyFilter.type) filter.type = policyFilter.type;
    dataProvider.getList('business-rules-templates', { filter, pagination: { page: 1, perPage: 500 }, sort: { field: 'country', order: 'ASC' } }).then(({ data }) => setPolicies(data));
  }, [currentTabId, policyFilter, filtersReady, dataProvider]);

  useEffect(() => {
    if (currentTabId !== 4 || !filtersReady) return;
    const filter: Record<string, string> = {};
    if (requestTypeFilter.country) filter.country = requestTypeFilter.country;
    dataProvider.getList('request-type-templates', { filter, pagination: { page: 1, perPage: 500 }, sort: { field: 'country', order: 'ASC' } }).then(({ data }) => setRequestTypes(data));
  }, [currentTabId, requestTypeFilter, filtersReady, dataProvider]);

  useEffect(() => {
    if (currentTabId !== 5 || !filtersReady) return;
    const filter: Record<string, string> = {};
    if (requestSubtypeFilter.country) filter.country = requestSubtypeFilter.country;
    if (requestSubtypeFilter.requestTypeTemplateUuid) filter.requestTypeTemplateUuid = requestSubtypeFilter.requestTypeTemplateUuid;
    dataProvider.getList('request-subtype-templates', { filter, pagination: { page: 1, perPage: 500 }, sort: { field: 'country', order: 'ASC' } }).then(({ data }) => setRequestSubtypes(data));
  }, [currentTabId, requestSubtypeFilter, filtersReady, dataProvider]);

  const resetHolidayFilter = () => setHolidayFilter({ country: String(record?.country ?? ''), type: '', paid: '' });
  const resetScheduleFilter = () => setScheduleFilter({ country: String(record?.country ?? ''), scheduleType: '' });
  const resetPolicyFilter = () => setPolicyFilter({ country: String(record?.country ?? ''), type: '' });
  const resetRequestTypeFilter = () => setRequestTypeFilter({ country: String(record?.country ?? '') });
  const resetRequestSubtypeFilter = () => setRequestSubtypeFilter({ country: String(record?.country ?? ''), requestTypeTemplateUuid: '' });

  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView title={<ContextTitle context="country" />}>
        <TabbedShowLayout>
          {/* ── General ── */}
          <Tab label="General">
            <TextField source="country" label="Country Code" />
            <TextField source="cssUrl" label="CSS URL" emptyText="—" />
            <TextField source="payrollUrl" label="Payroll URL" emptyText="—" />
            <TextField source="payrollUrlAuthStrategy" label="Auth Strategy" emptyText="—" />
            <BooleanField source="monthlyAccrualEnabled" label="Monthly accrual enabled" />
            <EditButton />
          </Tab>

          {/* ── Holidays ── */}
          <Tab label="Holidays">
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <MuiTextField label="Country" size="small" value={holidayFilter.country} onChange={(e) => setHolidayFilter((f) => ({ ...f, country: e.target.value }))} />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Type</InputLabel>
                  <MuiSelect label="Type" value={holidayFilter.type} onChange={(e) => setHolidayFilter((f) => ({ ...f, type: String(e.target.value) }))}>
                    <MenuItem value="">All</MenuItem>
                    {typeOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Paid</InputLabel>
                  <MuiSelect label="Paid" value={holidayFilter.paid} onChange={(e) => setHolidayFilter((f) => ({ ...f, paid: String(e.target.value) }))}>
                    {PAID_OPTIONS.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <Button variant="outlined" size="small" onClick={resetHolidayFilter}>
                  Reset
                </Button>
                <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={() => redirect('create', 'holiday-templates')}>
                  + Create
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>All Day</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Repeat</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holidays.map((h) => (
                    <TableRow key={h.id} hover>
                      <TableCell>{h.country}</TableCell>
                      <TableCell>{h.name}</TableCell>
                      <TableCell>{h.type}</TableCell>
                      <TableCell>{h.level}</TableCell>
                      <BoolCell value={h.allDay} />
                      <BoolCell value={h.paid} />
                      <BoolCell value={h.repeatAnnually} />
                      <TableCell>{formatHolidayValue(h.startTime, h.allDay)}</TableCell>
                      <TableCell>{formatHolidayValue(h.endTime, h.allDay)}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => redirect('edit', 'holiday-templates', h.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {holidays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Tab>

          {/* ── Schedule Templates ── */}
          <Tab label="Schedule Templates">
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <MuiTextField label="Country" size="small" value={scheduleFilter.country} onChange={(e) => setScheduleFilter((f) => ({ ...f, country: e.target.value }))} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Schedule Type</InputLabel>
                  <MuiSelect label="Schedule Type" value={scheduleFilter.scheduleType} onChange={(e) => setScheduleFilter((f) => ({ ...f, scheduleType: String(e.target.value) }))}>
                    <MenuItem value="">All</MenuItem>
                    {scheduleTypeChoices.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <Button variant="outlined" size="small" onClick={resetScheduleFilter}>
                  Reset
                </Button>
                <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={() => redirect('create', 'schedule-templates')}>
                  + Create
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Recurrence</TableCell>
                    <TableCell>Total Duration</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.country ?? '(global)'}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.scheduleType}</TableCell>
                      <TableCell>{s.recurrencePeriod ?? '—'}</TableCell>
                      <TableCell>{s.totalDuration}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => redirect('edit', 'schedule-templates', s.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {schedules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Tab>

          {/* ── Pay Policies ── */}
          <Tab label="Pay Policies">
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <MuiTextField label="Country" size="small" value={policyFilter.country} onChange={(e) => setPolicyFilter((f) => ({ ...f, country: e.target.value }))} />
                <MuiTextField label="Rule Type" size="small" value={policyFilter.type} onChange={(e) => setPolicyFilter((f) => ({ ...f, type: e.target.value }))} />
                <Button variant="outlined" size="small" onClick={resetPolicyFilter}>
                  Reset
                </Button>
                <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={() => redirect('create', 'business-rules-templates')}>
                  + Create
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Rule Type</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.country}</TableCell>
                      <TableCell>{p.type}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => redirect('edit', 'business-rules-templates', p.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {policies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Tab>
          {/* ── Request Type Templates ── */}
          <Tab label="Request Type Templates">
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <MuiTextField label="Country" size="small" value={requestTypeFilter.country} onChange={(e) => setRequestTypeFilter((f) => ({ ...f, country: e.target.value }))} />
                <Button variant="outlined" size="small" onClick={resetRequestTypeFilter}>
                  Reset
                </Button>
                <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={() => redirect('create', 'request-type-templates')}>
                  + Create
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestTypes.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.country}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {t.name}
                          <AddToLokaliseButton translationKey={t.name} />
                        </Box>
                      </TableCell>
                      <BoolCell value={t.active} />
                      <TableCell>
                        <Button size="small" onClick={() => redirect('edit', 'request-type-templates', t.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {requestTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Tab>

          {/* ── Request Subtype Templates ── */}
          <Tab label="Request Subtype Templates">
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <MuiTextField label="Country" size="small" value={requestSubtypeFilter.country} onChange={(e) => setRequestSubtypeFilter((f) => ({ ...f, country: e.target.value }))} />
                <MuiTextField label="Type Template UUID" size="small" value={requestSubtypeFilter.requestTypeTemplateUuid} onChange={(e) => setRequestSubtypeFilter((f) => ({ ...f, requestTypeTemplateUuid: e.target.value }))} />
                <Button variant="outlined" size="small" onClick={resetRequestSubtypeFilter}>
                  Reset
                </Button>
                <Button variant="contained" size="small" sx={{ ml: 'auto' }} onClick={() => redirect('create', 'request-subtype-templates')}>
                  + Create
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Name ID</TableCell>
                    <TableCell>Translation Key</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestSubtypes.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.country}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.nameId}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {s.translationKey}
                          <AddToLokaliseButton translationKey={s.translationKey} />
                        </Box>
                      </TableCell>
                      <BoolCell value={s.active} />
                      <TableCell>
                        <Button size="small" onClick={() => redirect('edit', 'request-subtype-templates', s.id)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {requestSubtypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                        No records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Tab>
        </TabbedShowLayout>
      </ShowView>
    </ShowContextProvider>
  );
};

export default CountryConfigsShow;
